import Product from '@grafikr/shopify-typescript/type/object/Product';
import Variant from '@grafikr/shopify-typescript/type/object/Variant';
import { component } from 'picoapp';
import formatMoney from 'utils/format-money';
import OptionSelector from 'utils/option-selector';
import { repaint } from 'utils/node';
import { hide as hideOverlay, show as showOverlay } from 'utils/overlay';
import modifyCart from 'lib/modify-cart';
import loadScript from 'utils/load-script';

export default component(async (node) => {
  interface ProductConfig {
    isPreorder: boolean,
    klaviyoBisEnabled: boolean,
    selectedVariant: number,
    stockWarningLimit: number,
    isBulkProduct: boolean
  }

  const config: ProductConfig = JSON.parse(node.dataset.config);
  const {
    isPreorder, klaviyoBisEnabled, stockWarningLimit, selectedVariant, isBulkProduct,
  } = config;

  const productData: Product = JSON.parse(node.querySelector('[data-product-data]').textContent);

  interface VariantData {
    id: number,
    inventory_quantity: number,
    bulk_size?: number
  }

  const variantDataObjects: VariantData[] = JSON.parse(node.querySelector('[data-variant-data]').textContent);

  const optionItemActiveClass = 'product__option__item--active';
  const stickySelectionActiveClass = 'product__sticky-selection--active';
  const stockDotBaseClass = 'w-3 h-3 rounded-full';
  const btnDisabledClass = 'button--disabled';
  const btnSuccessClass = 'button--success';
  const btnWarningClass = 'button--warning';
  const btnDangerClass = 'button--danger';

  const bulkStrings = theme.strings.product.bulk;
  const formStrings = theme.strings.product.form;
  const stockStrings = theme.strings.product.stock;

  const form = node.querySelector<HTMLFormElement>('[data-form]');
  const mobileOptions = node.querySelector<HTMLElement>('[data-mobile-options]');
  const mobileOptionsCloseBtn = node.querySelector('[data-mobile-options-close-btn]');
  const mobileOptionsEditBtn = node.querySelector<HTMLButtonElement>('[data-mobile-options-edit-btn]');
  const mobileOptionsInfo = node.querySelector<HTMLElement>('[data-mobile-options-info]');
  const mobileOptionsStartBtn = node.querySelector<HTMLButtonElement>('[data-mobile-options-start-btn]');
  const mobileSubmit = node.querySelector<HTMLElement>('[data-mobile-submit]');
  const options = Array.from(node.querySelectorAll('[data-option]'));
  const optionItems = Array.from(node.querySelectorAll<HTMLElement>('[data-option-item]'));
  const optionTitles = Array.from(node.querySelectorAll<HTMLElement>('[data-option-title]'));
  const prices = node.querySelectorAll('[data-prices]');
  const qtyComponents = node.querySelectorAll('[data-qty-component]');
  const qtyInputs = node.querySelectorAll<HTMLInputElement>('[data-qty-input]');
  const stickySelection = node.querySelector<HTMLElement>('[data-sticky-selection]');
  const stockInfos = node.querySelectorAll('[data-stock-info]');
  const submitBtns = node.querySelectorAll<HTMLButtonElement>('[data-submit-btn]');
  const klaviyoBisBtns = node.querySelectorAll<HTMLElement>('[data-klaviyo-bis-btn]');
  const shippingInfo = node.querySelector<HTMLElement>('[data-shipping-info]');
  const sizeguideBtns = node.querySelectorAll('[data-sizeguide-btn]');
  const productInfos = Array.from(node.querySelectorAll<HTMLElement>('[data-product-info]'));
  const infoBtn = node.querySelector('[data-info-btn]');
  const recommendations = node.querySelector('[data-recommendations]');
  const recommendationsBtn = node.querySelector('[data-recommendations-btn]');
  const bulkSize = node.querySelector<HTMLElement>('[data-bulk-size]');
  const sizeDrawerBtn = node.querySelector<HTMLElement>('[data-sizedrawer-btn]');

  const initOptions = () => {
    const updateBulkSize = (variant: Variant) => {
      const variantDataObject = variantDataObjects.find((object) => object.id === variant.id);

      if (!variantDataObject && variantDataObject.bulk_size) {
        return;
      }

      bulkSize.dataset.bulkSize = variantDataObject.bulk_size.toString();
    };

    const updateStockInfos = (variant: Variant) => {
      let html: string;
      const variantDataObject = variantDataObjects.find((object) => object.id === variant.id);

      if (variant.available) {
        if (variantDataObject.inventory_quantity > stockWarningLimit) {
          html = `<span class="${stockDotBaseClass} bg-success"></span>
            <span>
              ${stockStrings.variantInStock.replace('%variantName%', variant.title)}
            </span>`;
        } else {
          /* eslint-disable @typescript-eslint/indent */
          html = `<span class="${stockDotBaseClass} bg-warning"></span>
            <span class="text-warning">
              ${stockStrings.variantInStockLimited
              .replace('%variantName%', variant.title)
              .replace('%count%', variantDataObject.inventory_quantity.toString())}
            </span>`;
          /* eslint-enable @typescript-eslint/indent */
        }
      } else {
        html = `<span class="${stockDotBaseClass} bg-danger"></span>
          <span class="text-danger">
            ${stockStrings.variantSoldOut.replace('%variantName%', variant.title)}
          </span>`;
      }

      stockInfos.forEach((stockInfo) => {
        stockInfo.innerHTML = html;
      });
    };

    const updateSubmitBtns = (variant: Variant) => {
      const removeClasses = (btn: HTMLElement) => {
        [btnDisabledClass, btnSuccessClass, btnWarningClass, btnDangerClass].forEach((btnClass) => {
          btn.classList.remove(btnClass);
        });
      };

      submitBtns.forEach((btn) => {
        removeClasses(btn);

        const btnText = btn.querySelector('[data-btn-text]');

        if (variant) {
          if (variant.available) {
            btn.classList.add(isPreorder ? btnWarningClass : btnSuccessClass);
            btn.disabled = false;

            btnText.textContent = isPreorder ? formStrings.preorder : formStrings.addToCart;

            btn.hidden = false;
          } else {
            btn.classList.add(btnDangerClass);
            btn.disabled = !klaviyoBisEnabled;

            btnText.textContent = formStrings.soldOut;

            if (klaviyoBisEnabled) {
              btn.hidden = true;
            }
          }
        } else {
          btn.classList.add(btnDangerClass);
          btn.disabled = true;

          btnText.textContent = formStrings.unavailable;

          btn.hidden = false;
        }
      });
    };

    const updateBulkSubmitBtns = () => {
      const selectedBulkSize = parseInt(bulkSize.dataset.bulkSize, 10);
      const selectedTotal = Array.from(qtyComponents)
        .reduce((total, qty: HTMLSelectElement) => total + parseInt(qty.value, 10), 0);

      if (selectedTotal > selectedBulkSize) {
        submitBtns.forEach((btn) => {
          const amount = selectedBulkSize - selectedTotal;

          btn.querySelector('[data-btn-text]').textContent = formStrings.bulkSizeExceeded
            .replace('{{ amount }}', (-amount).toString());
          btn.disabled = true;
        });
      } else if (selectedTotal < selectedBulkSize) {
        submitBtns.forEach((btn) => {
          const amount = selectedBulkSize - selectedTotal;

          btn.querySelector('[data-btn-text]').textContent = formStrings.bulkSizeNotReached
            .replace('{{ amount }}', amount.toString());
          btn.disabled = true;
        });
      } else {
        submitBtns.forEach((btn) => {
          btn.querySelector('[data-btn-text]').textContent = formStrings.addToCart;
          btn.disabled = false;
        });
      }
    };

    const updateKlaviyoBisBtns = (variant: Variant) => {
      if (!klaviyoBisEnabled) {
        return;
      }

      const hideBtns = variant?.available;

      klaviyoBisBtns.forEach((btn) => {
        btn.style.display = ''; // remove display: none; set by klaviyo
        btn.hidden = hideBtns;
      });
    };

    const updateShippingInfo = (variant: Variant) => {
      shippingInfo.hidden = !variant || !variant?.available;
    };

    const updatePrices = (variant: Variant) => {
      prices.forEach((price) => {
        const bulk = price.hasAttribute('data-per-item');
        const selectedBulkSize = bulk && parseInt(bulkSize.dataset.bulkSize, 10);

        /* eslint-disable @typescript-eslint/indent */
        price.innerHTML = `<span class="product-prices__current ${variant.price < variant.compare_at_price
          ? 'product-prices__current--highlighted'
          : ''}">
          ${formatMoney(bulk ? variant.price / selectedBulkSize : variant.price)}
        </span>
        ${(() => {
            let html = '';
            if (variant.price < variant.compare_at_price) {
              const saveAmount = variant.compare_at_price - variant.price;
              const savePercentage = Math.floor((saveAmount / variant.compare_at_price) * 100);
              html = `<span class="product-prices__compare">${formatMoney(bulk ? variant.compare_at_price / selectedBulkSize : variant.compare_at_price)}</span>
                    ${!bulk ? theme.templates.savePercentageBadge.replace('%amount%', savePercentage.toString()) : ''}`;
            }
            return html;
          })()}`;
        /* eslint-enable @typescript-eslint/indent */
      });
    };

    const updateBulkQuantitySelectors = () => {
      const setQtyOptions = (min: number, max: number, qtyComponent: HTMLSelectElement) => {
        qtyComponent.innerHTML = '';

        for (let i = min; i <= max; i += 1) {
          const option = document.createElement('option');
          option.value = i.toString();
          option.text = i.toString();
          qtyComponent.add(option);
        }
      };

      qtyComponents.forEach((qtyComponent: HTMLSelectElement) => {
        const selectedBulkSize = parseInt(bulkSize.dataset.bulkSize, 10);
        setQtyOptions(0, selectedBulkSize, qtyComponent);
      });

      updateBulkSubmitBtns();
    };

    const initBulkQuantitySelectors = () => {
      qtyComponents.forEach((qtyComponent) => {
        updateBulkQuantitySelectors();
        qtyComponent.addEventListener('change', () => updateBulkSubmitBtns());
      });
    };

    const onVariantSelected = (variant: Variant | null) => {
      if (!isPreorder) {
        updateStockInfos(variant);
        updateShippingInfo(variant);
      }

      if (isBulkProduct) updateBulkSize(variant);

      updateSubmitBtns(variant);
      updateKlaviyoBisBtns(variant);
      updatePrices(variant);

      if (isBulkProduct) updateBulkQuantitySelectors();

      if (variant) {
        mobileOptionsStartBtn.hidden = true;
        mobileSubmit.hidden = false;
      }
    };

    OptionSelector(`#product-${productData.id}`, {
      data: productData,
      onVariantSelected,
      showSelectors: false,
      selectFirstAvailableVariant: false,
    });

    const optionSelects = Array.from(node.querySelectorAll<HTMLSelectElement>('[data-option-select]'));

    optionItems.forEach((item) => {
      const { name, value } = item.dataset;

      item.addEventListener('click', () => {
        const option = item.closest('[data-option]');
        const tooltip = option.querySelector<HTMLElement>('[data-tooltip]');
        tooltip.hidden = true;

        const allItems = optionItems.filter((otherItem) => (
          otherItem.dataset.name === name
        ));

        allItems.forEach((otherItem) => {
          otherItem.classList[otherItem.dataset.value === value ? 'add' : 'remove'](optionItemActiveClass); // sync mobile <-> desktop
        });

        const titles = optionTitles.filter((title) => (
          title.dataset.name === name
        ));

        titles.forEach((title) => {
          title.innerHTML = theme.strings.product.options[`${name}Selected`]?.replace('%selectedValue%', value)
            || value;
        });

        const matchingOptionSelect = optionSelects.find((select) => (
          select.dataset.name === name
        ));

        matchingOptionSelect.value = value;
        matchingOptionSelect.dispatchEvent(new Event('change'));

        sessionStorage.setItem(name, value);
      });
    });

    const handleClick = (event: Event) => {
      if (!mobileOptions.contains(event.target as HTMLElement)) {
        node.dispatchEvent(new Event('mobileOptions:hide'));
        document.removeEventListener('click', handleClick);
      }
    };

    const hideMobileOptions = () => {
      document.removeEventListener('click', handleClick);
      mobileOptions.hidden = true;
      mobileOptionsInfo.hidden = false;
      hideOverlay();
      stickySelection.classList.remove('z-50');
      document.documentElement.style.overflow = '';
    };

    const showMobileOptions = () => {
      document.documentElement.style.overflow = 'hidden';
      stickySelection.classList.add('z-50');
      showOverlay();
      mobileOptionsInfo.hidden = true;
      mobileOptions.hidden = false;

      setTimeout(() => {
        if (!mobileOptions.hidden) {
          document.addEventListener('click', handleClick);
        }
      }, 0);
    };

    node.addEventListener('mobileOptions:hide', hideMobileOptions);

    [mobileOptionsStartBtn, mobileOptionsEditBtn].forEach((btn) => {
      btn?.addEventListener('click', () => {
        if (mobileOptions.hidden) {
          showMobileOptions();
        } else {
          hideMobileOptions();
        }
      });
    });

    mobileOptionsCloseBtn.addEventListener('click', () => {
      hideMobileOptions();
    });

    if (isBulkProduct) {
      initBulkQuantitySelectors();
      updateBulkSubmitBtns();
    }
  };

  const preselectOptions = () => {
    if (selectedVariant != null) {
      return;
    }

    Object.entries(sessionStorage).forEach(([name, value]) => {
      const matchingOptionItem = optionItems.find((item) => (
        item.dataset.name === name && item.dataset.value === value
      ));

      if (matchingOptionItem) {
        matchingOptionItem.click();
      }
    });
  };

  const initStickySelection = () => {
    const show = () => {
      stickySelection.hidden = false;
      repaint(stickySelection);
      stickySelection.classList.add(stickySelectionActiveClass);
    };

    const hide = () => {
      stickySelection.classList.remove(stickySelectionActiveClass);

      stickySelection.addEventListener('transitionend', () => {
        if (!stickySelection.classList.contains(stickySelectionActiveClass)) {
          stickySelection.hidden = true;
        }
      }, { once: true });
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          hide();
        } else {
          show();
        }
      });
    });

    observer.observe(form);
  };

  const initQty = () => {
    qtyComponents.forEach((qtyComponent) => {
      const decreaseBtn = qtyComponent.querySelector('[data-qty-decrease-btn]');
      const increaseBtn = qtyComponent.querySelector('[data-qty-increase-btn]');
      const input: HTMLInputElement = qtyComponent.querySelector('[data-qty-input]');

      const min = parseInt(input.min, 10);
      const max = parseInt(input.max, 10);

      const updateValue = (modifier: number) => {
        const currentValue = parseInt(input.value, 10);

        let updatedValue = currentValue + modifier;
        if (updatedValue < min) updatedValue = min;
        if (updatedValue > max) updatedValue = max;

        input.value = updatedValue.toString();
        input.dispatchEvent(new Event('change'));
      };

      decreaseBtn.addEventListener('click', () => updateValue(-1));
      increaseBtn.addEventListener('click', () => updateValue(1));

      input.addEventListener('change', () => {
        qtyInputs.forEach((otherInput) => {
          otherInput.value = input.value; // sync mobile <-> desktop
        });
      });
    });
  };

  const initSubmit = () => {
    if (isBulkProduct && !bulkSize.dataset.bulkSize) {
      submitBtns.forEach((btn) => {
        btn.querySelector('[data-btn-text]').textContent = bulkStrings.selectAmount;
        btn.disabled = true;
      });
    }

    submitBtns.forEach((btn) => {
      btn.addEventListener('click', (event) => {
        event.preventDefault();

        const formData = new FormData(form);

        if (isBulkProduct) {
          qtyComponents.forEach((qty: HTMLSelectElement) => {
            const value = parseInt(qty.value, 10);
            if (value > 0) {
              formData.append(`properties[${qty.dataset.size}]`, value.toString());
            }
          });
        }

        if (formData.get('id')) {
          const btnHtml = btn.innerHTML;
          btn.innerHTML = theme.icons.loading;

          modifyCart('add', formData, () => {
            btn.innerHTML = btnHtml;
          });
        } else {
          const unselectedOption = options.find((option) => (
            Array.from(option.querySelectorAll('[data-option-item]')).find((item) => (
              !item.classList.contains(optionItemActiveClass)
            ))
          ));

          const tooltip = unselectedOption.querySelector<HTMLElement>('[data-tooltip]');
          tooltip.hidden = false;
        }
      });
    });
  };

  const initSizeguide = () => {
    // check if EasySize app is installed
    const easysizeScript = Array.from(document.querySelectorAll('script')).find((script) => (
      script.src.includes('easysize')
    ));

    if (!easysizeScript) {
      sizeguideBtns.forEach((btn) => {
        btn.remove();
      });

      console.error('EasySize app is not installed'); // eslint-disable-line no-console
    }

    let easysizeBtn = document.querySelector<HTMLElement>('#easysize_button');

    // trigger click on btn injected by app
    const initSizeguideBtns = () => {
      sizeguideBtns.forEach((btn) => {
        btn.addEventListener('click', () => {
          easysizeBtn.click();
        });
      });
    };

    // wait for app to inject modal btn
    if (easysizeBtn) {
      initSizeguideBtns();
    } else {
      const interval = setInterval(() => {
        easysizeBtn = document.querySelector<HTMLElement>('#easysize_button');

        if (easysizeBtn) {
          clearInterval(interval);
          initSizeguideBtns();
        }
      }, 300);
    }
  };

  const initInfoBtn = () => {
    if (!productInfos.length) {
      return;
    }

    const productInfo = productInfos.find((info) => info.dataset.context === 'mobile');

    const description = productInfo.querySelector('[data-description]');
    const descriptionBtn = description.querySelector<HTMLElement>('[data-accordion-btn]');
    const descriptionContent = description.querySelector<HTMLElement>('[data-accordion-content]');

    const firstAccorditionBtn = productInfo.querySelector<HTMLElement>('[data-accordion-btn]');
    const firstAccordionContent = productInfo.querySelector<HTMLElement>('[data-accordion-content]');

    infoBtn?.addEventListener('click', () => {
      const scrollPosition = productInfo.getBoundingClientRect().top + window.scrollY;
      window.scrollTo(0, scrollPosition);
      if (descriptionBtn && descriptionContent) {
        if (descriptionContent.hidden) descriptionBtn.click();
      } else if (firstAccorditionBtn && firstAccordionContent) {
        if (firstAccordionContent.hidden) firstAccorditionBtn.click();
      }
    });
  };

  const initRecommendationsBtn = () => {
    if (!recommendations) {
      return;
    }

    recommendationsBtn?.addEventListener('click', () => {
      const scrollPosition = recommendations.getBoundingClientRect().top + window.scrollY;
      window.scrollTo(0, scrollPosition);
    });
  };

  const initJudgeme = async () => {
    await loadScript('https://cdn.judge.me/shopify_v2.js');
  };

  const initSizeDrawerBtn = () => {
    sizeDrawerBtn?.addEventListener('click', () => document.dispatchEvent(new Event('sizeDrawer:open')));
  };

  const init = () => {
    initOptions();
    preselectOptions();
    initStickySelection();
    if (!isBulkProduct) initQty();
    initSubmit();
    initSizeguide();
    initInfoBtn();
    initRecommendationsBtn();
    initJudgeme();
    initSizeDrawerBtn();
  };

  init();
});
